import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import Status from "../Status";
import {
  Body,
  borderColor,
  borderStyle,
  borderWidth,
  Button,
  colorCanceledOrder,
  colorInProgress,
  DialogContent,
  Divider,
  Label,
  SubTitle,
} from "../";

import {
  addCommaToEvery3Digits,
  changeLoadingType,
  changePackagingType,
  changeVehicleWeight,
  convertDate,
  convertOption,
  sortOrder,
} from "../../lib";
import OrderService from "../../services/order";
import { useNavigate } from "react-router";
import { useFormContext } from "react-hook-form";

const headCells = [
  { id: "no", label: "운송번호|상태", sortable: true },
  { id: "departureDate", label: "날짜/시간", sortable: true },
  { id: "departureName", label: "장소명", sortable: false },
  { id: "departureAddressBase", label: "주소", sortable: false },
  { id: "vehicleType", label: "차량 정보", sortable: false },
  { id: "createdAt", label: "요청 시간", sortable: true },
];

const useStyles = makeStyles(() => ({
  pagination: {
    flexShrink: 0,
  },
  dialogContent: {
    padding: 0,
    overflow: "hidden",
    width: 1008,
  },
  freightInfoContainer: {
    width: 337,
    paddingLeft: 20,
    paddingTop: 32,
  },
  vehiclePlaceContainer: {
    width: 337,
    borderLeftStyle: borderStyle,
    borderLeftWidth: borderWidth,
    borderLeftColor: borderColor,
    paddingLeft: 20,
    paddingTop: 32,
  },
  mapContainer: {
    width: 333,
    paddingLeft: 20,
  },

  driverContainer: {
    marginBottom: 10,
  },

  listContainer: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    marginBottom: 18,
  },
  listRow: {
    display: "flex",
    marginTop: 12,
  },
  listRow2: {
    display: "flex",
    marginTop: 6,
  },
  listLabel: {
    width: 90,
    flexShrink: 0,
  },
  map: {
    width: "100%",
    height: "100%",
  },
  cancel: {
    position: "absolute",
    bottom: 23,
    right: 14,
    width: 55,
    height: 28,
    opacity: 0.5,
  },
  inprogress: {
    textAlign: "center",
    fontSize: 20,
    fontWeight: "bold",
    color: colorInProgress,
    padding: 20,
  },
  cancleOrder: {
    textAlign: "center",
    fontSize: 20,
    fontWeight: "bold",
    color: colorCanceledOrder,
    padding: 20,
  },
  completedDescription: {
    textAlign: "center",
    marginTop: -14,
    color: "rgba(104, 104, 104, 0.6)",
    fontSize: 13,
  },
}));

const MapView = (props: any) => {
  const classes = useStyles();
  const { mapOptions } = props;

  useEffect(() => {
    new window.naver.maps.Map("map", mapOptions);
  }, []);

  return <div id="map" className={classes.map} />;
};

const NoticeWithoutDriverInfo = (props: any) => {
  const classes = useStyles();
  const { order } = props;
  const status = order.status;

  return (
    <>
      <li>
        {status === 0 && <div className={classes.inprogress}>배차중</div>}
        {status === 10 && <div className={classes.cancleOrder}>주문취소</div>}
        {status === 11 && <div className={classes.cancleOrder}>배차취소</div>}
      </li>
      {order.status === 0 && (
        <li>
          <div className={classes.completedDescription}>
            배차가 완료되면 제공됩니다.
          </div>
        </li>
      )}
    </>
  );
};

const Detail = (props: any) => {
  const classes = useStyles();
  const { order, close } = props;

  const { watch } = useFormContext();

  const driver = order.driver;
  const navigate = useNavigate();

  const downloadReceipt = async (index: number): Promise<void> => {
    const orderService = new OrderService(navigate);
    const response = await orderService.downloadReceipt(order.key, index);
    const url = URL.createObjectURL(
      new Blob([response.data], { type: response.data.type })
    );
    const link = document.createElement("a");
    const filename = `인수증_${String(order.departureDate).substring(0, 10)}_${
      order.arrivalName
    }_${order.receiptCount}-${index + 1}.jpg`;

    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
  };

  const GetImages = (props: any) => {
    const { count } = props;

    if (count === 0) return <></>;

    const rendering = () => {
      const list = [];

      for (let i = 0; i < count; i++) {
        list.push(
          <img
            src={process.env.PUBLIC_URL + "/static/images/receipt.png"}
            style={{ width: 40, marginRight: 10, cursor: "pointer" }}
            onClick={() => downloadReceipt(i)}
          />
        );
      }

      return list;
    };

    return <body style={{ backgroundColor: "#fff" }}>{rendering()}</body>;
  };

  const mapOptions = {
    center: new window.naver.maps.LatLng(37.3595704, 127.105399),
    zoom: 10,
  };

  const cancel = useCallback(() => {
    alert("주문/배차 취소는 운영자에게 연락해 주세요.");
  }, []);

  return (
    <Dialog
      disableEscapeKeyDown
      maxWidth={"lg"}
      aria-labelledby="confirmation-dialog-title"
      open={order}
      onClose={(e: any, reason: string) => {
        if (reason === "backdropClick") return;
        close();
      }}
    >
      <DialogContent className={classes.dialogContent}>
        <Grid container direction={"row"}>
          <Grid item className={classes.freightInfoContainer}>
            <SubTitle>{"운송정보"}</SubTitle>
            <ul className={classes.listContainer}>
              <li className={classes.listRow}>
                <span className={classes.listLabel}>
                  <Label>{"운송번호"}</Label>
                </span>
                <Body>{order.no}</Body>
              </li>
              <li className={classes.listRow}>
                <span className={classes.listLabel}>
                  <Label>{"상태"}</Label>
                </span>
                <Status status={order.status} />
              </li>
              <li className={classes.listRow}>
                <span className={classes.listLabel}>
                  <Label>{"요청차량"}</Label>
                </span>
                <Body>
                  {order.vehicleType} /{" "}
                  {changeVehicleWeight(order.vehicleType, order.vehicleWeight)}
                </Body>
              </li>
              <li className={classes.listRow}>
                <span className={classes.listLabel}>
                  <Label>
                    {"품목 "}
                    {order.vehicleType !== "오토바이" &&
                      order.totalLength > 0 &&
                      "/ 길이"}
                    {order.vehicleType !== "오토바이" && " / 중량"}{" "}
                  </Label>
                </span>
                <Body>
                  {order.item}{" "}
                  {order.vehicleType !== "오토바이" &&
                    order.totalLength > 0 &&
                    `/ ${order.totalLength}M`}{" "}
                  {order.vehicleType !== "오토바이" &&
                    `/ ${order.totalWeight}톤`}{" "}
                </Body>
              </li>
              <li className={classes.listRow}>
                <span className={classes.listLabel}>
                  <Label>{"포장방법"}</Label>
                </span>
                <Body>{changePackagingType(order.packagingType)}</Body>
              </li>
              <li className={classes.listRow}>
                <span className={classes.listLabel}>
                  <Label>{"상차 / 하차 방법"}</Label>
                </span>
                <Body>
                  {changeLoadingType(order.loadingType)} /{" "}
                  {changeLoadingType(order.unloadingType)}
                </Body>
              </li>
              <li className={classes.listRow}>
                <span className={classes.listLabel}>
                  <Label>{"옵션"}</Label>
                </span>
                <Body>
                  {convertOption(
                    order.optionBackDoorOpen,
                    order.optionForklift,
                    order.optionShuttle,
                    order.optionMultiple,
                    order.orderCount,
                    order.orderInterval,
                    order.optionMixedLoading,
                    order.optionCashOnDelivery
                  )}
                </Body>
              </li>
              <li className={classes.listRow}>
                <span className={classes.listLabel}>
                  <Label>{"기사님 요청사항"}</Label>
                </span>
                <Body>{order.requestNoteForDriver}</Body>
              </li>
              <li className={classes.listRow}>
                <span className={classes.listLabel}>
                  <Label>{"카고콜 요청사항"}</Label>
                </span>
                <Body>{order.requestNoteForAdmin}</Body>
              </li>
              <li className={classes.listRow}>
                <span className={classes.listLabel}>
                  <Label>{"비고"}</Label>
                </span>
                <Body>{order.memoForCustomer}</Body>
              </li>
              <li className={classes.listRow}>
                <span className={classes.listLabel}>
                  <Label>{"정산 전달사항"}</Label>
                </span>
                <Body>{order.settlementMemoForCustomer}</Body>
              </li>
              <li className={classes.listRow}>
                <span className={classes.listLabel}>
                  <Label>{"운송료"}</Label>
                </span>
                <Body>
                  {order.chargeUnit === 0
                    ? "미정"
                    : addCommaToEvery3Digits(order.chargeUnit) + "원"}
                </Body>
              </li>
              <li className={classes.listRow}>
                <span className={classes.listLabel}>
                  <Label>{"인수증"}</Label>
                </span>
                <GetImages count={order.receiptCount} />
              </li>
            </ul>
          </Grid>
          <Grid item className={classes.vehiclePlaceContainer}>
            <Grid container direction={"column"} spacing={1}>
              <Grid item className={classes.driverContainer}>
                <Box display="flex" justifyContent="space-between" width="100%">
                  <SubTitle>{"차주 정보"}</SubTitle>
                  <IconButton size="small" onClick={close}>
                    <CloseIcon />
                  </IconButton>
                </Box>

                <ul className={classes.listContainer}>
                  {driver &&
                  driver.phone &&
                  (order.status === 1 ||
                    order.status === 2 ||
                    order.status === 3) ? (
                    <>
                      <li className={classes.listRow2}>
                        <span className={classes.listLabel}>
                          <Label>{"이름"}</Label>
                        </span>
                        <Body>{driver.name ? driver.name : "미정"}</Body>
                      </li>
                      <li className={classes.listRow2}>
                        <span className={classes.listLabel}>
                          <Label>{"연락처"}</Label>
                        </span>
                        <Body>{driver.phone ? driver.phone : "미정"}</Body>
                      </li>
                      <li className={classes.listRow2}>
                        <span className={classes.listLabel}>
                          <Label>{"차량번호"}</Label>
                        </span>
                        <Body>
                          {driver.vehicleNumber ? driver.vehicleNumber : "미정"}
                        </Body>
                      </li>
                      <li className={classes.listRow2}>
                        <span className={classes.listLabel}>
                          <Label>{"차량"}</Label>
                        </span>
                        <Body>
                          {driver.vehicleType !== ""
                            ? driver.vehicleType +
                              " / " +
                              changeVehicleWeight(
                                driver.vehicleType,
                                driver.vehicleWeight
                              )
                            : "미정"}
                        </Body>
                      </li>
                    </>
                  ) : (
                    <NoticeWithoutDriverInfo order={order} />
                  )}
                </ul>
                <Divider />
              </Grid>
              <Grid item>
                <SubTitle>{"출발지"}</SubTitle>
                <ul className={classes.listContainer}>
                  <li className={classes.listRow2}>
                    <span className={classes.listLabel}>
                      <Label>{"장소명"}</Label>
                    </span>
                    <Body>{order.departureName}</Body>
                  </li>
                  <li className={classes.listRow2}>
                    <span className={classes.listLabel}>
                      <Label>{"주소"}</Label>
                    </span>
                    <Body>
                      {order.departureAddressBase +
                        " " +
                        order.departureAddressDetail}
                    </Body>
                  </li>
                  <li className={classes.listRow2}>
                    <span className={classes.listLabel}>
                      <Label>{"연락처"}</Label>
                    </span>
                    <Body>{order.departurePhone}</Body>
                  </li>
                  <li className={classes.listRow2}>
                    <span className={classes.listLabel}>
                      <Label>{"날짜/시간"}</Label>
                    </span>
                    <Body>{convertDate(new Date(order.departureDate))}</Body>
                  </li>
                </ul>
              </Grid>
              {console.log(order.waypoints)}
              {order?.waypoints &&
                order.waypoints.map((v: any, i: number) => {
                  return (
                    <Grid item key={i}>
                      <SubTitle>{`경유지${i + 1}`}</SubTitle>
                      <ul className={classes.listContainer}>
                        <li className={classes.listRow2}>
                          <span className={classes.listLabel}>
                            <Label>{"장소명"}</Label>
                          </span>
                          <Body>{v.name}</Body>
                        </li>
                        <li className={classes.listRow2}>
                          <span className={classes.listLabel}>
                            <Label>{"주소"}</Label>
                          </span>
                          <Body>{v.addressBase + " " + v.addressDetail}</Body>
                        </li>
                        <li className={classes.listRow2}>
                          <span className={classes.listLabel}>
                            <Label>{"연락처"}</Label>
                          </span>
                          <Body>{v.phone}</Body>
                        </li>
                        <li className={classes.listRow2}>
                          <span className={classes.listLabel}>
                            <Label>{"메모"}</Label>
                          </span>
                          <Body>{v.memo}</Body>
                        </li>
                        <li className={classes.listRow2}>
                          <span className={classes.listLabel}>
                            <Label>{"날짜/시간"}</Label>
                          </span>
                          <Body>{convertDate(new Date(v.date))}</Body>
                        </li>
                      </ul>
                    </Grid>
                  );
                })}

              <Grid item>
                <SubTitle>{"도착지"}</SubTitle>
                <ul className={classes.listContainer}>
                  <li className={classes.listRow2}>
                    <span className={classes.listLabel}>
                      <Label>{"장소명"}</Label>
                    </span>
                    <Body>{order.arrivalName}</Body>
                  </li>
                  <li className={classes.listRow2}>
                    <span className={classes.listLabel}>
                      <Label>{"주소"}</Label>
                    </span>
                    <Body>
                      {order.arrivalAddressBase +
                        " " +
                        order.arrivalAddressDetail}
                    </Body>
                  </li>
                  <li className={classes.listRow2}>
                    <span className={classes.listLabel}>
                      <Label>{"연락처"}</Label>
                    </span>
                    <Body>{order.arrivalPhone}</Body>
                  </li>
                  <li className={classes.listRow2}>
                    <span className={classes.listLabel}>
                      <Label>{"날짜/시간"}</Label>
                    </span>
                    <Body>{convertDate(new Date(order.arrivalDate))}</Body>
                  </li>
                </ul>
              </Grid>
            </Grid>
          </Grid>
          <Grid item className={classes.mapContainer}>
            <MapView mapOptions={mapOptions} />
          </Grid>
        </Grid>
        {(order.status === 0 || order.status === 1) && (
          <Button className={classes.cancel} onClick={cancel}>
            {order.status === 0 ? "주문취소" : "배차취소"}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

const EnhancedTableHead = (props: any) => {
  const { orderType, orderBy, onRequestSort, headCells } = props;

  const createSortHandler = useCallback(
    (property: any) => (event: any) => {
      onRequestSort(event, property);
    },
    [onRequestSort]
  );

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell: any) => (
          <TableCell
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? orderType : false}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? orderType : "asc"}
                onClick={headCell.sortable && createSortHandler(headCell.id)}
                hideSortIcon={!headCell.sortable}
              >
                <Label>{headCell.label}</Label>
              </TableSortLabel>
            ) : (
              <Label>{headCell.label}</Label>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export default function Content(props: any) {
  const classes = useStyles();
  const { orders } = props;

  const [order, setOrder] = useState<any | null>(null);
  const [orderType, setOrderType] = useState<string>("desc");
  const [orderBy, setOrderBy] = useState<string>("createdAt");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const handleRequestSort = useCallback(
    (event: any, property: any) => {
      const isAsc = orderBy === property && orderType === "asc";
      setOrderType(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [orderType, orderBy, setOrderType, setOrderBy]
  );

  const handleChangePage = useCallback(
    (e, newPage) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handleChangeRowsPerPage = useCallback(
    (e) => {
      setRowsPerPage(e.target.value);
      setPage(0);
    },
    [setRowsPerPage]
  );

  useEffect(() => {
    if (order) {
      console.log("운송조회 - Modal ON::");

      window.gtag("event", `운송조회-주문상세-ON`, {
        user_id: localStorage.getItem("customerName") || "none",
      });
    } else if (order === null) {
      console.log("운송조회 - Modal OFFF::");
      window.gtag("event", `운송조회-주문상세-OFF`, {
        user_id: localStorage.getItem("customerName") || "none",
      });
    }
  }, [order]);

  return (
    <div>
      {order && <Detail order={order} close={() => setOrder(null)} />}
      <Table size="medium">
        <EnhancedTableHead
          classes={classes}
          orderBy={orderBy}
          orderType={orderType}
          onRequestSort={handleRequestSort}
          headCells={headCells}
        />
        <TableBody>
          {orders &&
            orders
              .sort(sortOrder(orderType, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((o: any, i: number) => (
                <TableRow key={i} hover onClick={() => setOrder(o)}>
                  <TableCell>
                    {o.no}
                    <br />
                    <Status status={o.status} />
                  </TableCell>
                  <TableCell>
                    {convertDate(new Date(o.departureDate))}
                    {o.waypoints.map((v: any, i: number) => {
                      return (
                        <span key={i}>
                          <br />
                          {convertDate(new Date(v.date))}
                        </span>
                      );
                    })}
                    <br />
                    {convertDate(new Date(o.arrivalDate))}
                  </TableCell>
                  <TableCell>
                    {o.departureName}
                    {o.waypoints.map((v: any, i: number) => {
                      return (
                        <span key={i}>
                          <br />
                          {v.name}
                        </span>
                      );
                    })}
                    <br />
                    {o.arrivalName}
                  </TableCell>
                  <TableCell>
                    {o.departureAddressBase + " " + o.departureAddressDetail}
                    {o.waypoints.map((v: any, i: number) => {
                      return (
                        <span key={i}>
                          <br />
                          {v.addressBase + " " + v.addressDetail}
                        </span>
                      );
                    })}
                    <br />
                    {o.arrivalAddressBase + " " + o.arrivalAddressDetail}
                  </TableCell>
                  <TableCell>
                    {o.driver &&
                      o.driver.vehicleType !== "" &&
                      o.driver.vehicleType +
                        " / " +
                        changeVehicleWeight(
                          o.driver.vehicleType,
                          o.driver.vehicleWeight
                        )}
                    <br />
                    {o.driver && o.driver.vehicleNumber}
                  </TableCell>
                  <TableCell>{convertDate(new Date(o.createdAt))}</TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={orders.length} // 전체 row 갯수
        page={page} // zero-based index of the current page
        rowsPerPage={rowsPerPage} // number of rows per page
        rowsPerPageOptions={[10, 20, 50]} // rows per page select field
        onPageChange={handleChangePage} // Callback fired when the page is changed
        onRowsPerPageChange={handleChangeRowsPerPage} // Callback fired when the number of rows per page is changed
        className={classes.pagination}
      />
    </div>
  );
}
